import { bootWorkspace, q, reapScript, seedSaltScript } from '@engineer-os/engine';
import { BrowserWorkspace } from './workspace.js';
import { CWD_FILE, LEVELS_DIR, WORKSPACE_ROOT } from './types.js';
import { ALWAYS_KEEP, FORGET_STAMP, RESTORE_FAILED, restoreScript, SEEDED_STAMP, stageWork } from './worksave.js';
const NO_RESTORE = { take: () => null, settled: () => undefined };
export class TarballSeeder {
    levels;
    saltFor;
    onPhase;
    restorer;
    /**
     * `levels` is `VmManifest.levels`: the mission ids baked, and their seed values.
     * `saltFor` answers with a per-learner salt for the levels that have one.
     * `onPhase` is told what is happening; it must not throw and may do nothing.
     */
    constructor(levels, saltFor = async () => null, onPhase = () => undefined, restorer = NO_RESTORE) {
        this.levels = levels;
        this.saltFor = saltFor;
        this.onPhase = onPhase;
        this.restorer = restorer;
    }
    async seed(ws, mission) {
        // Refused here, before the workspace is wiped, so a level the image does
        // not have cannot empty the one the learner was in. The question this
        // asks is only "is there a tarball": the manifest is the list of what was
        // baked, and it stays that even for the levels whose values have moved
        // out of it.
        const baked = this.levels[mission.id];
        if (baked === undefined) {
            throw new Error(`level "${mission.id}" was not baked into this image, so it has no workspace to open`);
        }
        /**
         * The mission's own values first, the manifest's second.
         *
         * A paid level's seed values are the answers to it, and the manifest is
         * public — so scripts/build-missions.mjs moves them onto the mission,
         * which for levels 20 and up arrives through `/api/missions` with the
         * entitlement check in front of it. `mission.seed` is therefore the
         * normal source for a paid level and the manifest is the normal source
         * for a free one, and neither is special-cased here: this takes whichever
         * it was given.
         *
         * The fallback is what makes the two halves of a deploy independent. An
         * app built after this change, handed a manifest built before it, finds
         * the values where they used to be and works; the reverse cannot happen,
         * because the manifest and the bundle ship together.
         */
        const values = mission.seed ?? baked;
        // Before the workspace is touched, and it throws rather than falling back.
        // A checkpoint's whole claim is that its repository is nobody else's, so a
        // level that cannot get its salt has no assessment to open: seeding it
        // from the shared bytes would hand somebody twenty-five minutes of work
        // that the server will refuse, which is worse than saying so now.
        const salt = await this.saltFor(mission.id);
        const tarball = `${LEVELS_DIR}/${mission.id}.tar.gz`;
        const cwd = mission.cwd ? `${WORKSPACE_ROOT}/${mission.cwd}` : WORKSPACE_ROOT;
        // Staged into /tmp before the one exec below, which then lays it over the
        // fresh seed. A failure to stage is a level that opens fresh, not one that
        // does not open.
        let work = this.restorer.take(mission.id);
        if (work) {
            try {
                await stageWork(ws, work);
            }
            catch {
                work = null;
                this.restorer.settled(mission.id, false);
            }
        }
        const script = (restoring) => [
            // The previous level's stamp describes a workspace about to be deleted.
            // Gone first, so that a capture landing mid-seed finds nothing to take.
            FORGET_STAMP,
            // Everything the last level left running, before the files it was
            // running out of are deleted underneath it. The learner's shell is
            // spared and restarted at the end of this same script instead.
            reapScript(WORKSPACE_ROOT),
            // A file is unlinked by writing its *directory*, so a level that took
            // the write bit off one of its own directories cannot be deleted —
            // `rm -rf` stops on "Permission denied" and the next level extracts on
            // top of what is left. The learner owns every one of these, so the bits
            // can simply be put back first: u+rwX, with the capital X so files do
            // not become executable on the way out. This is the format's job and
            // not the level's, for the reason the sweep above is: a level about
            // permissions would otherwise have to remember to undo itself, and the
            // one that forgot would break the *next* level rather than its own.
            `chmod -R u+rwX ${WORKSPACE_ROOT} 2>/dev/null`,
            // Dotfiles too, and never the mount point itself. `rm` complains when
            // the workspace is already empty, which is not an error here.
            `rm -rf ${WORKSPACE_ROOT}/* ${WORKSPACE_ROOT}/.[!.]* 2>/dev/null`,
            // The one step that must not fail quietly: everything after it would
            // otherwise succeed against an empty workspace.
            `tar xzf ${q(tarball)} -C ${WORKSPACE_ROOT} || exit 1`,
            // The learner's own starting commit, folded into this same exec rather
            // than run as a second one: the channel round trip costs more than the
            // commit does (docs/browser.md — git itself is tens of milliseconds; the
            // 214-483 ms is the trip). In a subshell so that its `cd` cannot move
            // the lines after it.
            ...(salt ? [`( ${seedSaltScript(salt, mission.cwd || '.')} ) || exit 1`] : []),
            // The reflogs the salt just wrote, dated now: kept by every capture (worksave.ts, ALWAYS_KEEP).
            ...(salt ? [`printf '%s\\n' ${q(`./${mission.cwd ? `${mission.cwd}/` : ''}.git/logs/HEAD`)} ${q(`./${mission.cwd ? `${mission.cwd}/` : ''}.git/logs/refs/heads/main`)} > ${ALWAYS_KEEP}`] : []),
            // The starting state is complete: everything newer than this is the
            // learner's (worksave.ts). After the salt, which is the seed's and not
            // theirs, and before a restore, which is theirs.
            `touch ${SEEDED_STAMP}`,
            ...(restoring ? [restoreScript()] : []),
            // Where the next ttyS0 login shell starts. Ending the current one makes
            // inittab respawn it there; the exec channel is on ttyS1 and is not
            // disturbed. The signal is SIGHUP, not pkill's default SIGTERM, because
            // an *interactive* bash ignores SIGTERM: with the default the shell
            // survives, the prompt stays in the previous level's directory, and the
            // learner's first `git` command answers "not a git repository". SIGHUP
            // is what a terminal going away sends, and bash exits on it.
            // `pkill` exits 1 when it matched nothing — the first level after a
            // restore has no shell to end yet — so the request ends in `true`
            // and its exit status is `tar`'s, which is the one that matters.
            `echo ${q(cwd)} > ${CWD_FILE}`,
            'pkill -HUP -t ttyS0',
            'true',
        ].join('\n');
        this.onPhase(mission.id, 'unpacking');
        const load = async (restoring) => {
            const r = await ws.exec(script(restoring), { timeoutMs: 60_000 });
            if (r.code !== 0) {
                throw new Error(`could not load level "${mission.id}" (exit ${r.code}): ${r.stderr.trim() || r.stdout.trim()}`);
            }
            return r;
        };
        const r = await load(work !== null);
        if (work) {
            // A restore that stopped halfway leaves a tree that is neither the
            // learner's nor the level's, so the level is seeded again from nothing.
            const restored = !r.stdout.includes(RESTORE_FAILED);
            if (!restored)
                await load(false);
            this.restorer.settled(mission.id, restored);
        }
        // The workspace's own record of where the shell now is, so attaching a
        // terminal afterwards does not restart it a second time.
        if (ws instanceof BrowserWorkspace)
            ws.shellStartedIn(cwd);
        // The level's processes, started on the machine that will be asked about
        // them. The values are normalised first, so that a `{{seed.PORT}}` a boot
        // script overwrites and one it inherits have been through the same trim.
        //
        // The same emptiness test `bootWorkspace` makes, made again here rather
        // than reported from inside it, because this is a statement about the
        // wait and that is a statement about the level: announcing a step that
        // returns without doing anything is the one way this narration could lie.
        if (mission.boot !== undefined && mission.boot.trim() !== '')
            this.onPhase(mission.id, 'starting');
        return bootWorkspace(ws, mission, normaliseSeed(values));
    }
}
/**
 * The manifest's values, as `seedWorkspace` would have returned them on the
 * server: trailing whitespace trimmed, keys matching `[A-Za-z0-9_]+`. The
 * bake writes exactly what the seed script echoed, and the same check must
 * pass on both drivers, so the normalisation happens here rather than being
 * trusted to the build.
 */
export function normaliseSeed(values) {
    const seed = {};
    for (const [key, value] of Object.entries(values)) {
        if (!/^[A-Za-z0-9_]+$/.test(key))
            continue;
        seed[key] = value.trim();
    }
    return seed;
}
/**
 * `KEY=value` lines, one per line, `@@` already stripped by the bake — the
 * format of the work directory's `<id>.seed` files, which the image build
 * turns into `manifest.levels` and nothing else reads any more.
 *
 * Split on the *first* `=` only, because a value may contain one, and trim
 * the value exactly as `seedWorkspace` does on the server — or the same
 * check passes on one driver and fails on the other.
 */
export function parseSeed(stdout) {
    const seed = {};
    for (const line of stdout.split('\n')) {
        const eq = line.indexOf('=');
        if (eq <= 0)
            continue;
        const key = line.slice(0, eq);
        if (!/^[A-Za-z0-9_]+$/.test(key))
            continue;
        seed[key] = line.slice(eq + 1).trim();
    }
    return seed;
}
//# sourceMappingURL=seeder.js.map